/**
 * Hook to load chart data from JSON file for testing purposes
 * In production, this will be replaced with API endpoints
 */

import { useMemo } from "react";
import sampleChartData from "./sampleChartData.json";
import {
  RetirementReadinessData,
  AUMByProductData,
  ContributionRateData,
  SocialSecurityData,
  RetirementIncomeSourcesData,
  FEGLIRiskData,
  AgeDistributionData,
  RolloverOpportunityData,
  EmployerMatchData,
  LocationInsightsData,
  PlanTypeSpreadData,
  LongevityStressData,
} from "./widgetCalculations";

export interface ChartDataFromJSON {
  retirementReadiness: RetirementReadinessData;
  aumByProduct: AUMByProductData;
  contributionRate: ContributionRateData;
  socialSecurity: SocialSecurityData;
  retirementIncomeSources: RetirementIncomeSourcesData;
  fegliRisk: FEGLIRiskData;
  ageDistribution: AgeDistributionData;
  rolloverOpportunity: RolloverOpportunityData;
  employerMatch: EmployerMatchData;
  locationInsights: LocationInsightsData;
  planTypeSpread: PlanTypeSpreadData;
  longevityStress: LongevityStressData;
}

/**
 * Transforms JSON data to match widget calculation output formats
 */
function transformJSONData(): ChartDataFromJSON {
  const json = sampleChartData as any;

  return {
    retirementReadiness: json.widgetA_retirementReadiness,
    aumByProduct: json.widgetB_aumByProduct,
    contributionRate: json.widgetC_contributionRate,
    socialSecurity: json.widgetD_socialSecurity,
    retirementIncomeSources: {
      sources: json.widgetE_retirementIncomeSources.sources,
      totalAverage: json.widgetE_retirementIncomeSources.sources.reduce(
        (sum: number, s: any) => sum + s.average,
        0
      ),
    },
    fegliRisk: {
      categories: json.widgetF_fegliRisk.riskCategories,
    },
    ageDistribution: {
      buckets: json.widgetG_ageDistribution.ageBuckets,
    },
    rolloverOpportunity: json.widgetH_rolloverOpportunity,
    employerMatch: {
      categories: json.widgetI_employerMatch.categories,
    },
    locationInsights: {
      locations: json.widgetJ_locationInsights.locations,
    },
    planTypeSpread: {
      categories: {
        FERS: json.widgetK_planTypeSpread.categories.find((c: any) => c.planType === "FERS")?.count || 0,
        CSRS: json.widgetK_planTypeSpread.categories.find((c: any) => c.planType === "CSRS")?.count || 0,
        Private: (json.widgetK_planTypeSpread.categories.find((c: any) => c.planType === "401k")?.count || 0) +
                 (json.widgetK_planTypeSpread.categories.find((c: any) => c.planType === "403b")?.count || 0),
        NoPlan: 0,
        Unknown: 0,
      },
      fersSubtypes: json.widgetK_planTypeSpread.fersSubtypes?.FERS ? {
        RAE: json.widgetK_planTypeSpread.fersSubtypes.FERS["FERS-RAE"] || 0,
        FRAE: json.widgetK_planTypeSpread.fersSubtypes.FERS["FERS-FRAE"] || 0,
        Standard: json.widgetK_planTypeSpread.fersSubtypes.FERS["FERS Basic"] || 0,
      } : undefined,
    },
    longevityStress: {
      atRiskCount: json.widgetL_longevityStress.atRiskCount,
      fundedCount: json.widgetL_longevityStress.fundedCount,
      avgYearsShort: json.widgetL_longevityStress.avgYearsShort,
      atRiskClients: [],
    },
  };
}

/**
 * Hook to get chart data from JSON file
 * Set useJSONData to true for testing, false for production (API)
 */
export function useChartData(useJSONData: boolean = true): ChartDataFromJSON | null {
  return useMemo(() => {
    if (!useJSONData) {
      return null; // Will use calculated data from clients
    }
    return transformJSONData();
  }, [useJSONData]);
}
